import React, { useState, useEffect } from 'react';
import Layout from "../layout/Layout";
import { Select, Input, Form, Badge } from "antd";
import ModalComponent from "../../../components/global/modal/ModalComponent.jsx";
import stateDistricts from '../../../assets/data/stateDistricts.jsx';
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import { AddStudentApi } from "../../../api/request/Student.js";
import toast from "react-hot-toast";
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FaCheckCircle, FaStar, FaArrowRight, FaRocket } from 'react-icons/fa';

const plans = [
    {
        name: "Starter Trial",
        duration: "3 Month",
        Yearly_price: "Free",
        Monthly_price: "Free",
        popular: false,
        featured: true,
        color: "from-green-500 to-emerald-600",
        details: [
            "Unlimited number of users with unrestricted access.",
            "Full access to the Web App, equipped with all core and premium features.",
            "Dedicated Admin Mobile Application for smooth administrative control.",
            "Specialized Mentor Mobile App to manage and guide student progress efficiently.",
            "Student Mobile App with personalized learning experiences.",
            "1 customized Email Domain Service to manage professional communication.",
            "Document Redundancy Policy ensuring secure backup for up to 7 years.",
            "Document Storage Capacity: 350 GB to store academic and administrative records safely.",
            "Complimentary Configuration Support for 3 Years to ensure a hassle-free setup and operational continuity.",
        ],
    },
    {
        name: "Core Plan",
        Yearly_price: "4,999",
        Monthly_price: "549",
        users: "100",
        popular: true,
        featured: false,
        color: "from-blue-500 to-indigo-600",
        details: [
            "Supports up to 100 active users including students, mentors, and administrators.",
            "Access to the Web App, designed to simplify academic and administrative tasks.",
            "1 Email Domain Service for seamless communication across your institution.",
            "Secure Document Redundancy Policy for 1 Year, safeguarding essential academic records.",
            "Configuration Support provided for 1 Year to assist with integration and maintenance.",
        ],
    },
    {
        name: "Elevate Plan",
        Yearly_price: "6,499",
        Monthly_price: "599",
        users: "200",
        popular: false,
        featured: false,
        color: "from-purple-500 to-pink-600",
        details: [
            "Accommodates up to 200 users with full platform access.",
            "Complete Web App Access for managing academic workflows effectively.",
            "Access to the Mentor Mobile App, allowing mentors to guide, assess, and interact with students on the go.",
            "1 Professional Email Domain Service included.",
            "Document Storage up to 100 GB to securely handle learning materials and administrative documents.",
            "Document Redundancy Policy extended to 3 Years for better data protection and compliance.",
            "Configuration Support for 1 Years to ensure smooth platform operations and upgrades.",
        ],
    },
    {
        name: "Professional Plan",
        Yearly_price: "7,499",
        Monthly_price: "699",
        users: "600",
        popular: false,
        featured: false,
        color: "from-orange-500 to-red-600",
        details: [
            "Designed for up to 600 users, covering students, mentors, administrators, and external collaborators.",
            "Complete Web App Access including premium academic and administrative modules.",
            "Admin Mobile App for real-time management and monitoring of institutional activities.",
            "Mentor Mobile App to facilitate efficient mentor operations.",
            "Student Mobile App to deliver personalized learning and progress tracking.",
            "1 Custom Email Domain Service included to unify communication within the institution.",
            "Document Redundancy coverage for 5 Years ensuring reliable, long-term data security.",
            "Document Storage up to 200 GB to manage large-scale academic and institutional data securely.",
            "Full Configuration Support for 3 Years to back your institution's digital growth journey.",
        ],
    },
    {
        name: "Elevate Max Plan",
        Yearly_price: "9,999",
        Monthly_price: "899",
        users: "1000",
        popular: false,
        featured: false,
        color: "from-indigo-500 to-purple-600",
        details: [
            "Accommodates up to 1,000 users with full platform access.",
            "Full-featured Web App offering cutting-edge tools for administration, learning, and communication.",
            "Admin Mobile App providing total control at your fingertips.",
            "Mentor Mobile App ensuring continuous mentorship and collaboration.",
            "Student Mobile App delivering personalized, mobile-first learning experiences.",
            "1 Professional Email Domain Service bundled to streamline internal and external communication.",
            "Extended Document Redundancy for up to 7 Years, securing institutional memory for the long term.",
            "Massive Document Storage capacity of 350 GB for hassle-free management of academic and administrative content.",
            "Full Configuration Support for 3 Years included to guarantee seamless operational excellence.",
        ],
    },
    {
        name: "Flexible Plan",
        subtitle: "Pay-as-you-go",
        Yearly_price: "Based on Usage",
        Monthly_price: "Based on Usage",
        popular: false,
        featured: false,
        color: "from-teal-500 to-cyan-600",
        details: [
            "Unlimited users with flexible access to all Web App features.",
            "Full access to Admin, Mentor, and Student Mobile Applications without any upfront restrictions.",
            "1 Custom Email Domain Service provided.",
            "Document Redundancy Policy and Document Storage Capacity are fully customizable based on the institution's dynamic requirements.",
            "Pay only for the resources and features you actively use, providing maximum cost-efficiency."
        ],
    },
];

const { TextArea } = Input;
const { Option } = Select;

function Plans() {
    const [form] = Form.useForm();
    const [billingCycle, setBillingCycle] = useState("monthly");
    const [isOpenPlanModal, setIsOpenPlanModal] = useState(false);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [selectedState, setSelectedState] = useState('');
    const [districtOptions, setDistrictOptions] = useState([]);
    const [initialValues, setInitialValues] = useState({ 
        First_Name: '', Last_Name: '', Contact_Number: '', 
        State: '', District: '', Secondary_Contact: '', Address: '' 
    });

    useEffect(() => {
        AOS.init({ 
            duration: 1000, 
            once: true,
            offset: 100
        });
    }, []);

    const openAddPlanModal = () => {
        form.resetFields();
        setInitialValues({ 
            First_Name: '', Last_Name: '', Contact_Number: '', 
            State: '', District: '', Secondary_Contact: '', Address: '' 
        });
        setIsOpenPlanModal(true);
    }

    const handleFieldsChange = (_, allFields) => {
        const isChanged = allFields.some(field => field.touched);
        setIsFormChanged(isChanged);
    };

    const filterOption = (input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    const handleStateChange = (value) => {
        setSelectedState(value);
        setDistrictOptions(stateDistricts[value] || []);
    };

    const handleSubmit = async (values) => {
        setIsSubmitLoading(true);
        let params = { ...values };
        try {
            const { data } = await AddStudentApi(params);
            if (data?.success) {
                toast.success(data?.message || 'Thank you! We will contact you soon.');
                form.resetFields();
                setIsOpenPlanModal(false);
            }
        } catch (error) {
            toast.error(error?.response?.data?.error?.message || 'An error occurred');
        } finally {
            setIsSubmitLoading(false);
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>Pricing Plans - Educational Eternity | Choose Your Perfect Plan</title>
                <meta name="description" content="Choose the perfect pricing plan for your educational institution. From free trial to enterprise solutions, Educational Eternity offers flexible plans for schools and colleges of all sizes." />
                <meta name="keywords" content="Education ERP Pricing, School Management Software Plans, Education Software Pricing India, ERP System Cost" />
            </Helmet>

            {/* Hero Section */}
            <section className="relative py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `url(assets/images/home123.gif)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}></div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center max-w-4xl mx-auto" data-aos="fade-up">
                        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
                            Choose Your Perfect Plan
                        </h1>
                        <p className="text-xl md:text-2xl text-blue-100 mb-12">
                            Flexible pricing plans designed for educational institutions of all sizes. Start with a free trial or choose a plan that fits your needs.
                        </p>
                        
                        {/* Billing Toggle */}
                        <div className="flex justify-center items-center gap-4 mb-12">
                            <span className={`text-lg font-semibold ${billingCycle === "monthly" ? "text-white" : "text-blue-200"}`}>
                                Monthly
                            </span>
                            <button
                                onClick={() => setBillingCycle(billingCycle === "monthly" ? "yearly" : "monthly")}
                                className={`relative w-16 h-8 rounded-full transition-all duration-300 ${
                                    billingCycle === "yearly" ? "bg-gradient-to-r from-yellow-400 to-pink-500" : "bg-gray-300"
                                }`}
                            >
                                <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-all duration-300 ${
                                    billingCycle === "yearly" ? "transform translate-x-8" : ""
                                }`}></span>
                            </button>
                            <span className={`text-lg font-semibold ${billingCycle === "yearly" ? "text-white" : "text-blue-200"}`}>
                                Yearly
                                <span className="ml-2 text-sm bg-green-500 text-white px-2 py-1 rounded-full">Save 20%</span>
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Cards */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                        {plans.map((plan, index) => (
                            <div
                                key={index}
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                                className={`bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col relative ${
                                    plan.popular ? 'border-4 border-blue-500 scale-105 z-10' : 'border border-gray-200'
                                } ${plan.featured ? 'ring-4 ring-green-500 ring-opacity-50' : ''}`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                                        Most Popular
                                    </div>
                                )}
                                {plan.featured && (
                                    <div className="absolute -top-4 right-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                                        <FaRocket /> Free Trial
                                    </div>
                                )}
                                
                                <div className={`bg-gradient-to-r ${plan.color} text-white p-6 rounded-t-2xl`}>
                                    <h3 className="text-2xl md:text-3xl font-bold mb-2">{plan.name}</h3>
                                    {plan.subtitle && (
                                        <p className="text-blue-100 text-sm">{plan.subtitle}</p>
                                    )}
                                    {plan.duration && (
                                        <p className="text-blue-100 text-sm">{plan.duration}</p>
                                    )}
                                </div>
                                
                                <div className="p-8 flex-grow">
                                    <div className="mb-6">
                                        {billingCycle === "yearly" ? (
                                            plan.Yearly_price === "Free" || plan.Yearly_price === "Based on Usage" ? (
                                                <div>
                                                    <span className="text-4xl font-bold text-gray-900">{plan.Yearly_price}</span>
                                                </div>
                                            ) : (
                                                <div>
                                                    <span className="text-4xl font-bold text-gray-900">₹ {plan.Yearly_price}</span>
                                                    <span className="text-gray-600 ml-2">/year</span>
                                                </div>
                                            )
                                        ) : (
                                            plan.Monthly_price === "Free" || plan.Monthly_price === "Based on Usage" ? (
                                                <div>
                                                    <span className="text-4xl font-bold text-gray-900">{plan.Monthly_price}</span>
                                                </div>
                                            ) : (
                                                <div>
                                                    <span className="text-4xl font-bold text-gray-900">₹ {plan.Monthly_price}</span>
                                                    <span className="text-gray-600 ml-2">/month</span>
                                                </div>
                                            )
                                        )}
                                        {plan.users && (
                                            <div className="mt-3 text-sm text-gray-600">
                                                <span className="font-semibold">Up to {plan.users} users</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <ul className="space-y-3 mb-8">
                                        {plan.details.map((detail, i) => (
                                            <li key={i} className="flex items-start">
                                                <FaCheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" />
                                                <span className="text-gray-700 leading-relaxed">{detail}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    
                                    <button
                                        onClick={openAddPlanModal}
                                        className={`w-full py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 ${
                                            plan.popular
                                                ? `bg-gradient-to-r ${plan.color} text-white hover:shadow-lg`
                                                : plan.featured
                                                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg'
                                                : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                                        }`}
                                    >
                                        {plan.featured ? 'Start Free Trial' : 'Get Started'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Comparison */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16" data-aos="fade-up">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            All Plans Include
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Every plan comes with these essential features
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                        {[
                            "24/7 Technical Support",
                            "Regular Security Updates",
                            "Data Backup & Recovery",
                            "Mobile App Access",
                            "Email Domain Service",
                            "Document Management",
                            "User Training",
                            "Regular Feature Updates"
                        ].map((feature, idx) => (
                            <div
                                key={idx}
                                data-aos="fade-up"
                                data-aos-delay={idx * 50}
                                className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-gray-100 text-center"
                            >
                                <FaCheckCircle className="text-green-500 text-3xl mx-auto mb-3" />
                                <p className="text-gray-700 font-semibold">{feature}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
                <div className="container mx-auto px-4 text-center" data-aos="fade-up">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Not Sure Which Plan to Choose?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                        Our team is here to help you find the perfect plan for your institution. Schedule a free consultation today!
                    </p>
                    <button
                        onClick={openAddPlanModal}
                        className="px-8 py-4 bg-white text-blue-600 rounded-full text-lg font-bold shadow-2xl hover:shadow-white/50 transform hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto"
                    >
                        Schedule Free Consultation <FaArrowRight />
                    </button>
                </div>
            </section>

            {/* Modal */}
            <ModalComponent 
                isOpen={isOpenPlanModal} 
                setIsOpen={setIsOpenPlanModal} 
                title={'Get Started with Educational Eternity'} 
                width={900}
            >
                <Form 
                    className='w-full' 
                    autoComplete="off" 
                    form={form} 
                    initialValues={initialValues} 
                    onFinish={handleSubmit} 
                    onFieldsChange={handleFieldsChange}
                >
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                            <label className="text-base text-gray-700 font-semibold mb-2 block">
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <Form.Item name="First_Name" rules={[{ required: true, message: 'Please enter First Name.' }]}>
                                <Input placeholder="First name" className="w-full ps-4 py-3 rounded-lg" size="large" />
                            </Form.Item>
                        </div>
                        <div>
                            <label className="text-base text-gray-700 font-semibold mb-2 block">
                                Last Name <span className="text-red-500">*</span>
                            </label>
                            <Form.Item name="Last_Name" rules={[{ required: true, message: 'Please enter Last Name.' }]}>
                                <Input placeholder="Last name" className="w-full ps-4 py-3 rounded-lg" size="large" />
                            </Form.Item>
                        </div>
                        <div>
                            <label className="text-base text-gray-700 font-semibold mb-2 block">
                                Phone No <span className="text-red-500">*</span>
                            </label>
                            <Form.Item name="Contact_Number" rules={[{ required: true, message: 'Please enter phone number.' }]}>
                                <Input type="tel" placeholder="Phone no" className="w-full ps-4 py-3 rounded-lg" size="large" />
                            </Form.Item>
                        </div>
                        <div>
                            <label className="text-base text-gray-700 font-semibold mb-2 block">
                                State <span className="text-red-500">*</span>
                            </label>
                            <Form.Item name="State" rules={[{ required: true, message: "Please select a state." }]}>
                                <Select
                                    showSearch
                                    placeholder="Select a state"
                                    className="w-full"
                                    size="large"
                                    filterOption={filterOption}
                                    onChange={handleStateChange}
                                >
                                    {Object.keys(stateDistricts).map((state) => (
                                        <Option key={state} value={state}>
                                            {state}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                        <div>
                            <label className="text-base text-gray-700 font-semibold mb-2 block">
                                District <span className="text-red-500">*</span>
                            </label>
                            <Form.Item name="District" rules={[{ required: true, message: "Please select a district." }]}>
                                <Select
                                    showSearch
                                    placeholder="Select a district"
                                    className="w-full"
                                    size="large"
                                    filterOption={filterOption}
                                    disabled={!selectedState}
                                >
                                    {districtOptions.map((district) => (
                                        <Option key={district} value={district}>
                                            {district}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                        <div>
                            <label className="text-base text-gray-700 font-semibold mb-2 block">
                                Other Phone No (optional)
                            </label>
                            <Form.Item name="Secondary_Contact">
                                <Input type="tel" placeholder="Phone no" className="w-full ps-4 py-3 rounded-lg" size="large" />
                            </Form.Item>
                        </div>
                    </div>
                    <div className='mt-5'>
                        <label className='text-base text-gray-700 font-semibold mb-2 block'>
                            Address <span className="text-red-500">*</span>
                        </label>
                        <Form.Item name="Address" rules={[{ required: true, message: 'Please enter address.' }]}>
                            <TextArea 
                                className="w-full ps-4 py-3 rounded-lg" 
                                placeholder="Enter your complete address"
                                autoSize={{ minRows: 4, maxRows: 6 }} 
                            />
                        </Form.Item>
                    </div>
                    <div className="flex justify-center items-center mt-7 gap-5">
                        <LoadableButton
                            type="submit"
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold px-12 py-4 rounded-full hover:shadow-lg transition-all duration-300"
                            lable="Submit Request"
                            disabled={!isFormChanged || isSubmitLoading}
                            isLoading={isSubmitLoading}
                            loadingLable="Submitting..."
                        />
                    </div>
                </Form>
            </ModalComponent>
        </Layout>
    )
}

export default Plans;
